import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Paper,
  Avatar
} from '@mui/material';
import {
  Cable,
  Speed,
  AutoAwesome,
  CheckCircle,
  EmojiEvents,
} from '@mui/icons-material';
import Navigation from './Navigation';
import { mcpInfoContent } from '../content/pages/mcpInfo';

// Icon mapping
const IconMap: Record<string, React.ReactElement> = {
  AutoAwesome: <AutoAwesome />,
  Cable: <Cable />,
  Speed: <Speed />,
};

const McpInfo: React.FC = () => {
  const c = mcpInfoContent;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.9) 0%, rgba(111, 191, 115, 0.9) 30%, rgba(53, 122, 56, 0.9) 60%, rgba(76, 175, 80, 0.9) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.3,
          filter: 'brightness(0.7) contrast(1.2)',
          zIndex: 0
        }} />

        {/* Background Pattern Overlay */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 1
        }} />

        {/* Navigation */}
        <Navigation />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Cable sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 3 }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.95, mb: 2 }}>
              {c.hero.subtitle}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 800, mx: 'auto' }}>
              {c.hero.description}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Why MCP Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.whyMcp.title}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 900, mx: 'auto', mb: 6 }}>
            {c.whyMcp.cards.map((card, index) => (
              <Paper key={index} sx={{ 
                p: 4, 
                mb: 3, 
                bgcolor: index === 2 ? '#e8f5e9' : '#f5f5f5', 
                border: '2px solid #4caf50' 
              }}>
                <Typography variant="h6" sx={{ color: '#4caf50', mb: 2, fontWeight: 600 }}>
                  {card.title}
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#555' }}>
                  {card.description}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Grid container spacing={4}>
            {c.whyMcp.benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3, boxShadow: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 3, width: 64, height: 64 }}>
                    {IconMap[benefit.iconName]}
                  </Avatar>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#5A5856", fontSize: '1rem', lineHeight: 1.6 }}>
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Available MCP Servers */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.servers.title}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {c.servers.cards.map((server, index) => (
              <Card key={index} sx={{ p: 4, mb: 3, boxShadow: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', mb: 2 }}>
                  {server.title}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {server.description}
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5', fontFamily: 'monospace', fontSize: '0.95rem', color: '#d32f2f', mb: 2 }}>
                  {server.url}
                </Paper>
                <Typography variant="body2" sx={{ color: "#5A5856" }}>
                  <strong>Version:</strong> {server.version} | <strong>Status:</strong> {server.status}
                </Typography>
              </Card>
            ))}

            <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#5A5856', textAlign: 'center' }}>
              {c.servers.footer}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Authentication Methods */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {c.authMethods.title}
            </Typography>
            <Typography variant="body1" sx={{ color: "#5A5856", maxWidth: 700, mx: 'auto' }}>
              {c.authMethods.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {c.authMethods.methods.map((method, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper sx={{ p: 4, height: '100%', border: '1px solid #e0e0e0' }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {method.icon} {method.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: '#5A5856' }}>
                    <strong>Best for:</strong> {method.bestFor}
                  </Typography>
                  <Box component="ul" sx={{ pl: 3 }}>
                    {method.features.map((feature, idx) => (
                      <Box component="li" key={idx} sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 20, color: '#4caf50' }} />
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Setup Guide */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.setupGuide.title}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Box component="ol" sx={{ pl: 0, listStyle: 'none', counterReset: 'step' }}>
              {c.setupGuide.steps.map((step, index) => (
                <Box
                  component="li"
                  key={index}
                  sx={{
                    counterIncrement: 'step',
                    padding: '20px 0 20px 60px',
                    position: 'relative',
                    lineHeight: 1.7,
                    color: '#555',
                    '&::before': {
                      content: 'counter(step)',
                      position: 'absolute',
                      left: 0,
                      top: '16px',
                      background: '#4caf50',
                      color: 'white',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }
                  }}
                >
                  <Typography variant="body1">{step}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.pricing.title}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Paper sx={{
              p: 5,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e9 100%)',
              border: '2px solid #4caf50'
            }}>
              <EmojiEvents sx={{ fontSize: 60, color: '#ff9800', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 600, mb: 2 }}>
                {c.pricing.card.preTitle}
              </Typography>
              <Typography variant="h2" sx={{ color: '#4caf50', fontWeight: 700, my: 2 }}>
                {c.pricing.card.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
                {c.pricing.card.description}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', maxWidth: 500, mx: 'auto' }}>
                {c.pricing.card.footer}
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#4caf50', color: 'white', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.cta.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 6, opacity: 0.9, fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 700, mx: 'auto' }}>
              {c.cta.subtitle}
            </Typography>
            <Button
              variant="outlined"
              size="large"
              component="a"
              href="https://blog.familycast.app"
              target="_blank"
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 600,
                color: 'white',
                borderColor: 'white',
                borderWidth: 2,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderWidth: 2
                }
              }}
            >
              {c.cta.button}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              component="a"
              href="https://modelcontextprotocol.io"
              target="_blank"
              sx={{
                opacity: 0.7,
                color: 'white',
                textDecoration: 'none',
                '&:hover': { opacity: 1, textDecoration: 'underline' }
              }}
            >
              {c.footer.mcpDocs}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>•</Typography>
            <Typography
              variant="body2"
              component={Link}
              to="/docs"
              sx={{
                opacity: 0.7,
                color: 'white',
                textDecoration: 'none',
                '&:hover': { opacity: 1, textDecoration: 'underline' }
              }}
            >
              {c.footer.docs}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>•</Typography>
            <Typography
              variant="body2"
              component={Link}
              to="/contact"
              sx={{
                opacity: 0.7,
                color: 'white',
                textDecoration: 'none',
                '&:hover': { opacity: 1, textDecoration: 'underline' }
              }}
            >
              {c.footer.support}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center', mt: 2 }}>
            {c.footer.copyright}{new Date().getFullYear()}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default McpInfo;
